import React, { useEffect, useRef, useState } from 'react';
import { clearCurrentConversationId } from '../../services/compliance';
import { darkTheme } from '../../themes/dark';
import { env } from '../../../env';

interface Rule {
  _id: string;
  name: string;
  prompt: string;
  description?: string;
}

interface FolderData {
  id: string;
  name: string;
  description?: string;
  fileCount: number;
}

interface ChatWidgetProps {
  isVisible: boolean;
  complianceResult?: any; // Resultado da compliance
  newSubmission?: boolean; // Indica se acabou de ocorrer uma nova submissão
}

declare global {
  interface Window {
    ModernWebWidget: {
      initializeWidget: (config: any) => void;
      sendMessage?: (message: any) => void;
      open?: () => void;
      expand?: () => void;
      startNewConversation?: (conversationId: string) => void;
    };
  }
}

/**
 * Paleta enviada ao widget, derivada de `src/app/themes/dark.ts` (o tema do LIA).
 *
 * Fonte única de propósito: este bloco era duplicado nos dois pontos que chamam
 * `initializeWidget` — e o do caminho de recarga simplesmente não existia, o que fazia o widget
 * cair no `defaultTheme` dele (header branco COM TEXTO BRANCO). Duplicar convite a divergir.
 *
 * O que destes campos o widget de fato consome hoje:
 *   chat.primaryColor            → 60 usos  (botões, destaque, bolha do usuário)
 *   header.textColor             →  7 usos
 *   header.backgroundColor       →  5 usos
 *   chat.messageReceivedColor    →  2 usos  (bolha do assistente)
 *   chat.messageReceivedBorder   →  2 usos
 *   chat.secondaryColor          →  0 usos  (inerte; mantido por retrocompatibilidade)
 * O restante da interface do widget tem cor chumbada e NÃO responde a nada daqui —
 * é o que a spec `0002-tema-do-widget` (repo jurisdicao_webwidget) existe para resolver.
 */
/**
 * Cor de identidade do widget = a primária Hexium (petrol), vinda do tema.
 * Sem hex solto: `themes/brand.ts` é a fonte única da paleta.
 */
const WIDGET_BRAND = darkTheme.widgetBackground;   // palette.petrol — #0F445E

const WIDGET_STYLES = {
  /*
    ⚠️ TODO este bloco é OBSOLETO desde o redesenho do widget (ADR-0008, repo jurisdicao_webwidget).
    A faixa sólida do topo deixou de existir, então nada aqui pinta mais nada: o widget aceita os
    campos, registra e ignora. Mantido só porque remover não muda nada e o contrato é congelado.

    `backgroundColor` conserva UM efeito residual — servia de dica de claro/escuro — e é justamente
    por isso que ele agora vem depois de `chat.surface` em precedência. Ver a nota lá embaixo.
  */
  header: {
    backgroundColor: WIDGET_BRAND,
    border: `1px solid ${darkTheme.border}`,
    textColor: darkTheme.text,
    fontSize: '18px',
    height: '70px',
    height_logo: '45px',
    marginLeft_logo: '10px',
    logo: ''
  },
  chat: {
    inputMaxLenth: 2000,
    /*
      SUPERFÍCIE DO WIDGET — declarada explicitamente, e não por acidente.

      Sem este campo o widget escolhia a família de tema pela **dica** `header.backgroundColor`, que
      é a petrol. Petrol é cor escura, então o widget vinha inteiro em tema ESCURO — decidido por um
      campo obsoleto, que só existe ali para pintar uma faixa que já não existe. Ninguém pediu tema
      escuro; ele era efeito colateral.

      `surface` é o topo do degradê de fundo e `surfaceDeep` a base. Os dois vêm dos neutros Hexium
      (White → Gray Light), e são tons próximos de propósito: o texto atravessa todo o degradê, então
      o contraste tem de valer nas duas pontas.
    */
    surface: '#FFFFFF',
    surfaceDeep: '#F0F2F4',
    // Obsoletos (ADR-0008): o assistente não tem mais bolha. Aceitos e ignorados.
    messageReceivedColor: '#FFFFFF',
    messageReceivedBorder: '#E2E8F0',
    // A marca. Não pinta mais superfície — é ACENTO: botão de enviar, item ativo, foco, links.
    primaryColor: WIDGET_BRAND,
    // NOTA: `secondaryColor` não é consumido por nenhum componente do widget hoje
    // (0 ocorrências de theme.chat.secondaryColor). Mantido por retrocompatibilidade.
    secondaryColor: WIDGET_BRAND,
    fontFamily: 'ui-sans-serif, -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
    placeholder_chat: 'Como posso ajudar você hoje?',
    title: 'LIA',
    header_title: 'LIA',
    language: 'pt'
  }
} as const;

const ChatWidget: React.FC<ChatWidgetProps> = ({ isVisible, complianceResult, newSubmission = false }) => {
  const hasInitialized = useRef(false);
  const currentConversationId = useRef<string | null>(null);
  const previousSubmissionState = useRef(false);

  // Função para buscar arquivos de uma pasta
  const fetchFolderFiles = async (folderId: string): Promise<any[]> => {
    try {
      const token = localStorage.getItem('@App:token');
      const response = await fetch(`${env.API_URL}/api/folders/${folderId}/files`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      return Array.isArray(data) ? data : data.data || [];
    } catch (error) {
      console.error(`Erro ao buscar arquivos da pasta ${folderId}:`, error);
      return [];
    }
  };

  // Função para processar o resultado da compliance
  const processComplianceData = (data: any): string | null => {
    if (!data) return null;

    // Se o resultado vier diretamente no campo result
    if (data.result) {
      return data.result;
    }
    // Se o resultado vier no campo response
    else if (data.response) {
      return data.response;
    }
    // Se o próprio objeto for o resultado
    else if (typeof data === 'string') {
      return data;
    }
    // Se for um objeto, tentar converter para string
    else if (typeof data === 'object') {
      return JSON.stringify(data);
    }
    return null;
  };

  // Função para processar prompts selecionados
  const processSelectedPrompts = (prompts: Rule[]): string => {
    if (!prompts || prompts.length === 0) return '';
    
    return prompts.map(prompt => {
      return `**${prompt.name}**:\n${prompt.prompt}`;
    }).join('\n\n');
  };

  // Função para processar pastas selecionadas e obter arquivos
  const processSelectedFolders = async (folders: FolderData[]): Promise<any[]> => {
    if (!folders || folders.length === 0) return [];
    
    const allFiles: any[] = [];
    
    for (const folder of folders) {
      try {
        const files = await fetchFolderFiles(folder.id);
        const filesWithFolder = files.map(file => ({
          ...file,
          folderName: folder.name,
          folderId: folder.id
        }));
        allFiles.push(...filesWithFolder);
      } catch (error) {
        console.error(`Erro ao processar pasta ${folder.name}:`, error);
      }
    }
    
    return allFiles;
  };

  // Função para obter o ID do usuário e organização do localStorage
  const getUserData = (): { userId: string | null; organizationId: string | null } => {
    const storedUser = localStorage.getItem('@App:user');
    if (!storedUser) {
      console.error('Nenhum usuário encontrado no localStorage');
      return { userId: null, organizationId: null };
    }

    try {
      const userData = JSON.parse(storedUser);
      if (!userData || !userData.id) {
        console.error('ID do usuário não encontrado nos dados do localStorage');
        return { userId: null, organizationId: null };
      }
      
      const organizationId = userData.organizationId?.[0] || null;
      if (!organizationId) {
        console.error('ID da organização não encontrado nos dados do localStorage');
      }
      
      return {
        userId: userData.id,
        organizationId
      };
    } catch (error) {
      console.error('Erro ao obter dados do usuário do localStorage:', error);
      return { userId: null, organizationId: null };
    }
  };

  // Função para remover qualquer modal de confirmação na página
  // Função removeConfirmationModals foi removida conforme solicitado

  useEffect(() => {
    /*
      Marca que a conversa já foi zerada pelo caminho rápido nesta execução do efeito.

      Sem isto, o caminho rápido zerava o store e, mais adiante, o bloco de "widget já
      inicializado" chamava `initializeWidget` de novo — e essa função DESCARTA o container e cria um
      React root novo. O painel do chat desaparecia e reaparecia, o que no desktop é percebido como a
      página recarregando. Era o resto do pisca depois das três primeiras causas.
    */
    let conversaJaZerada = false;

    // Função para remover scripts antigos
    /*
      Remove só a tag do WIDGET, e por um motivo estreito: evitar acumular uma tag por abertura.

      Antes removia também `react`, `react-dom` e `styled-components`. Isso não liberava nada —
      remover a tag não descarrega o código já executado — e tinha um custo real: a guarda de carga
      lá embaixo perguntava pela TAG do react, então apagá-la fazia cada abertura do chat re-baixar
      React e ReactDOM do unpkg. Duas idas à rede por abrir o chat, para recarregar o que já estava
      na memória.

      `styled-components` saiu da lista porque não é mais carregado de CDN: o widget passou a
      empacotar a própria instância.
    */
    const removeOldScripts = () => {
      document.querySelectorAll('script[src*="modern-web-widget"]').forEach(script => script.remove());
    };

    // Função para remover elementos do widget
    const removeWidgetElements = () => {
      const selectors = [
        '#modern-web-widget-container',
        'iframe[id*="modern-web-widget"]',
        'div[id*="modern-web-widget"]',
        '.modern-web-widget',
        '[class*="modern-web-widget"]'
      ];
      
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(element => element.remove());
      });
    };

    // Função para configurar os event listeners do widget
    const setupWidgetEventListeners = () => {
      // Remover listeners anteriores se existirem
      window.removeEventListener('message', handleWidgetMessage);
      
      // Adicionar o novo listener
      window.addEventListener('message', handleWidgetMessage);
    };

    // Se houver uma nova submissão, limpa o conversationId atual para forçar uma nova conversa
    if (newSubmission && !previousSubmissionState.current) {
      console.log('Nova submissão detectada, limpando conversationId atual');
      
      // Limpar o conversationId atual para forçar uma nova conversa
      // mas não limpar o histórico completo de conversas
      clearCurrentConversationId(); 
      currentConversationId.current = null;
      
      /*
        CAMINHO RÁPIDO — zerar a conversa por dentro, sem recarregar nada.

        O widget passou a expor `startNewConversation`, que chama a ação do store: conversa nova com
        `messages: []`, novo id, e `inputText`/`formState`/`error` limpos. A árvore React continua
        montada e só re-renderiza — nada é baixado, e não há janela em branco.

        Antes daqui existir, zerar a conversa exigia destruir o widget e re-executar 1,2 MB de
        bundle, porque o store do Zustand é de módulo e sobrevive ao `initializeWidget`. Era isso que
        fazia a tela piscar ao entrar no chat.

        A detecção com `typeof` não é zelo excessivo: se este host subir ANTES do widget novo, o
        método não existe no bundle publicado, e chamá-lo direto quebraria o chat em produção. Sem a
        detecção, a ordem de deploy passaria a importar — com ela, não importa.
      */
      const widget = window.ModernWebWidget as any;
      if (widget && typeof widget.startNewConversation === 'function') {
        widget.startNewConversation();
        conversaJaZerada = true;
        const { userId, organizationId } = getUserData();
        if (userId && organizationId) {
          const novoId = `${organizationId}_${userId}_${new Date().getTime()}`;
          currentConversationId.current = novoId;
          localStorage.setItem('currentConversationId', novoId);
        }
      }

      /*
        CAMINHO ANTIGO — só para bundle publicado sem `startNewConversation`. Pisca, e é o preço de
        não depender da ordem de deploy.

        É `else if` e não um `if` com `return` cedo: sair do corpo do efeito antes do fim impediria o
        registro da função de limpeza desta execução, e o listener de `message` ficaria sem remoção.
      */
      else if (window.ModernWebWidget) {
        console.log('Forçando reinicialização completa do widget para nova conversa');

        // 1. Remover todos os elementos do widget existente
        removeWidgetElements();

        // 2. Remover referência global ao ModernWebWidget
        // @ts-ignore
        window.ModernWebWidget = undefined;

        // 3. Marcar como não inicializado
        hasInitialized.current = false;

        // 4. Obter os dados do usuário para o novo conversationId
        const { userId, organizationId } = getUserData();
        if (userId && organizationId) {
          // 5. Gerar novo conversationId
          const newConversationId = `${organizationId}_${userId}_${new Date().getTime()}`;
          console.log('Novo conversationId gerado para forçar nova conversa:', newConversationId);
          
          // 6. Armazenar no localStorage para persistência
          localStorage.setItem('currentConversationId', newConversationId);
          
          // 7. Aguardar um momento para DOM estabilizar
          setTimeout(() => {
            // 8. Carregar o script do widget novamente
            const widgetScript = document.createElement('script');
            // Faltava o nome do arquivo — este caminho carregava a origem crua do WIDGET_URL,
            // então ModernWebWidget nunca se redefinia e o guard abaixo falhava em silêncio.
            widgetScript.src = `${env.WIDGET_URL}/modern-web-widget.umd.js`;
            // Sem `crossOrigin` — ver a explicação no outro ponto de carga deste arquivo. Com o
            // atributo, e sem Access-Control-Allow-Origin na origem do widget, o browser bloqueia
            // o script e o widget não monta.
            widgetScript.async = true;
            widgetScript.onload = () => {
              // 9. Após carregar, inicializar widget com novo conversationId
              console.log('Widget recarregado, inicializando com novo conversationId...');
              
              if (window.ModernWebWidget && typeof window.ModernWebWidget.initializeWidget === 'function') {
                const widgetConfig = {
                  agentId: '679ab5f028fd2e21abd37162',
                  client_id: userId,
                  organization_id: organizationId,
                  conversationId: newConversationId,
                  forceNewConversation: true,  // Flag adicional para garantir nova conversa
                  preserveHistory: true,      // Manter o histórico anterior de conversas
                  
                  // Adicionar configurações de boas-vindas
                  welcome: {
                    enabled: true,
                    message: 'Nova submissão detectada! Esta é uma nova conversa, mas você ainda pode acessar seu histórico anterior.'
                  },

                  // Faltava aqui: sem `styles`, o widget caía no defaultTheme dele
                  // (header branco com texto branco) a cada recarga por nova submissão.
                  styles: WIDGET_STYLES
                };

                window.ModernWebWidget.initializeWidget(widgetConfig);
                hasInitialized.current = true;
                currentConversationId.current = newConversationId;
                
                // 10. Configurar listener para eventos do widget
                setupWidgetEventListeners();
                
                // 11. Enviar mensagem com prompts e arquivos selecionados após um tempo
                setTimeout(async () => {
                  if (window.ModernWebWidget && typeof window.ModernWebWidget.sendMessage === 'function') {
                    const complianceData = processComplianceData(complianceResult);
                    
                    // Processar dados do setup se disponíveis
                    const selectedPrompts = complianceResult?.selectedPrompts || JSON.parse(localStorage.getItem('selectedPrompts') || '[]');
                    const selectedFolders = complianceResult?.selectedFolders || JSON.parse(localStorage.getItem('selectedFolders') || '[]');
                    
                    let messageContent = '🔄 NOVA CONVERSA INICIADA\n\nUma nova submissão de compliance foi processada e esta é uma nova conversa.';
                    
                    // Adicionar prompts à mensagem se selecionados
                    if (selectedPrompts && selectedPrompts.length > 0) {
                      const promptsText = processSelectedPrompts(selectedPrompts);
                      messageContent += '\n\n📝 **PROMPTS SELECIONADOS:**\n' + promptsText;
                    }
                    
                    // Processar arquivos das pastas selecionadas
                    let attachedFiles: any[] = [];
                    if (selectedFolders && selectedFolders.length > 0) {
                      attachedFiles = await processSelectedFolders(selectedFolders);
                      if (attachedFiles.length > 0) {
                        messageContent += `\n\n📁 **ARQUIVOS ANEXADOS:** ${attachedFiles.length} arquivo(s) de ${selectedFolders.length} pasta(s)`;
                      }
                    }
                    
                    // Obter modelo selecionado
                    const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';
                    
                    const welcomeMessage = {
                      type: 'SYSTEM_MESSAGE',
                      content: messageContent,
                      conversationId: newConversationId,
                      compliance_data: complianceData,
                      selectedPrompts: selectedPrompts,
                      attachedFiles: attachedFiles,
                      model: selectedModel,
                      isNotification: true,
                      isHighlighted: true,
                      style: {
                        backgroundColor: '#f8f9fa',
                        borderLeft: '4px solid #c3232d',
                        padding: '10px',
                        marginBottom: '10px',
                        fontWeight: 'bold'
                      }
                    };
                    
                    window.ModernWebWidget.sendMessage(welcomeMessage);
                    
                    // Limpar dados do localStorage após envio
                    localStorage.removeItem('selectedPrompts');
                    localStorage.removeItem('selectedFolders');
                  }
                }, 2000);
              }
            };
            document.body.appendChild(widgetScript);
          }, 1000);
        }
      }
    }
    
    // Atualiza o estado anterior para evitar múltiplas limpezas
    previousSubmissionState.current = newSubmission;

    /*
      Limpa o que sobrou APENAS quando uma carga completa vem a seguir.

      Isto rodava sem condição, em toda execução do efeito, e tinha duas consequências ruins:

       1. Com o widget montado e funcionando, destruía-o à toa — metade do pisca vinha daqui.
       2. Pior, havia um caminho em que ele era destruído e NUNCA reconstruído: a guarda seguinte é
          `isVisible && !hasInitialized.current`, então com `hasInitialized` verdadeiro e sem a flag
          `newSubmission`, o widget desaparecia e nada o trazia de volta. Hoje isso ficava escondido
          porque a barra lateral sempre manda `isNewSubmission: true`; qualquer ponto de entrada novo
          que não mandasse cairia num chat vazio.

      `!hasInitialized.current` é exatamente a condição da carga completa logo abaixo — as duas
      passaram a andar juntas, que é o que evita destruir sem reconstruir.
    */
    if (!hasInitialized.current) {
      removeOldScripts();
      removeWidgetElements();
    }


    if (isVisible && !hasInitialized.current) {
      // Verificar se estamos na página compliance/pending e remover modais de confirmação
      const currentPath = window.location.pathname;
      if (currentPath.includes('compliance/pending')) {
        console.log('Detectada página compliance/pending. Removendo modais de confirmação...');
        // Remoção de modais foi desativada conforme solicitado
        console.log('Funcionalidade de remoção de modais desativada.');
      }
      
      const loadDependencies = async () => {
        try {
          // Primeiro, verifica se tem um userId e organizationId válidos
          const { userId, organizationId } = getUserData();
          if (!userId || !organizationId) {
            console.error('Não foi possível inicializar o widget: ID do usuário ou organização não disponível');
            return;
          }
          
          // Se o widget já estiver inicializado, apenas atualize o estado se necessário
          if (window.ModernWebWidget && typeof window.ModernWebWidget.initializeWidget === 'function' && hasInitialized.current) {
            /*
              Só reinicializa se o caminho rápido NÃO deu conta.

              `initializeWidget` descarta o container e cria um React root novo — é remonta completa
              do painel, e é justamente o que aparece como a página piscando. Quando o widget expõe
              `startNewConversation`, a conversa já foi zerada por dentro lá acima e remontar não
              acrescenta nada: só custa o pisca.

              O `newSubmission && !conversaJaZerada` cobre os dois mundos: bundle novo passa reto,
              bundle publicado antigo (sem o método) continua caindo aqui e remontando, que é o
              comportamento que existia — melhor piscar do que não abrir.
            */
            if (newSubmission && !conversaJaZerada) {
              // Reinicializar o widget com um novo conversationId
              initializeWidget(userId);
            }
            return;
          }

          // Funções para carregar scripts
          const loadReactScript = () => {
            return new Promise((resolve, reject) => {
              const reactScript = document.createElement('script');
              reactScript.src = 'https://unpkg.com/react@18/umd/react.production.min.js';
              reactScript.crossOrigin = '';
              reactScript.onload = resolve;
              reactScript.onerror = reject;
              document.body.appendChild(reactScript);
            });
          };

          const loadReactDOMScript = () => {
            return new Promise((resolve, reject) => {
              const reactDOMScript = document.createElement('script');
              reactDOMScript.src = 'https://unpkg.com/react-dom@18/umd/react-dom.production.min.js';
              reactDOMScript.crossOrigin = '';
              reactDOMScript.onload = resolve;
              reactDOMScript.onerror = reject;
              document.body.appendChild(reactDOMScript);
            });
          };

          /*
            O `styled-components` NÃO é mais carregado aqui.

            Ele vinha do unpkg e o widget o consumia pelo global `window.styled`. Sem pin de versão,
            o unpkg passou a servir a v6, cuja UMD publica o NAMESPACE do módulo no global
            (`{default, css, keyframes, …}`) em vez da função que a v5 publicava — e o widget, que
            chama `styled.div`, quebrou em produção sem nenhum deploy nosso.

            O pin + normalização do global resolvia num host só. A correção durável foi no widget:
            ele parou de externalizar `styled-components` e passou a empacotar a própria instância,
            então não existe mais contrato de global para manter aqui.

            ⚠️ Ordem de deploy importa: este host só pode subir sem o script depois que o bundle do
            widget empacotado estiver publicado. Um bundle antigo (externalizado) somado a este
            arquivo novo volta a dar `TypeError: he.div is not a function`.
          */

          const loadWidgetScript = () => {
            return new Promise((resolve, reject) => {
              const widgetScript = document.createElement('script');
              widgetScript.src = `${env.WIDGET_URL}/modern-web-widget.umd.js`;
              // NÃO ative `crossOrigin` aqui enquanto a origem do widget não devolver
              // `Access-Control-Allow-Origin`.
              //
              // Eu havia posto `crossOrigin = 'anonymous'` para que exceções do bundle deixassem de
              // aparecer como "Script error." sem stack. O efeito foi pior: com o atributo, o
              // browser passa a buscar o script em modo CORS, o Amplify do widget não manda o
              // header, e o script é BLOQUEADO — `onerror` dispara e o widget nunca monta. Trocar
              // diagnóstico por indisponibilidade é um mau negócio.
              //
              // Os scripts do unpkg acima usam `crossOrigin` sem problema porque o unpkg manda
              // `Access-Control-Allow-Origin: *`. O widget passou a mandar também (customHeaders no
              // amplify.yml dele), mas só volte a ativar isto aqui DEPOIS de confirmar o header em
              // produção — `curl -I` na URL do bundle, procurando access-control-allow-origin.
              widgetScript.async = true;
              widgetScript.onload = resolve;
              widgetScript.onerror = reject;
              document.body.appendChild(widgetScript);
            });
          };

          /*
            React e ReactDOM carregam só se os GLOBAIS não existirem — não se a tag não existir.

            A guarda era `document.querySelector('script[src*="react"]')`, e decidia as TRÊS cargas
            de uma vez. Como o `removeOldScripts()` acima apagava as tags do react em toda execução
            do efeito, a guarda sempre passava: cada abertura do chat re-baixava React e ReactDOM do
            unpkg — duas idas à rede, num CDN de terceiro — com os dois já carregados e funcionando.

            Remover uma tag de script NÃO descarrega o código que ela executou; só engana quem
            pergunta pela tag. O global é o indicador real de "já está carregado", e ele sobrevive à
            remoção da tag.
          */
          if (!(window as any).React) {
            await loadReactScript();
          }
          if (!(window as any).ReactDOM) {
            await loadReactDOMScript();
          }

          /*
            O bundle do widget, ao contrário, carrega SEMPRE — e isso é deliberado, não descuido.

            Re-executá-lo é o que zera o store do Zustand, que é de módulo e sobrevive a
            `initializeWidget` (esta cria um React root novo, mas não um módulo novo). É assim que
            este código começa uma conversa limpa.

            Trocar isso por uma reinicialização sem download é o que elimina o pisca por completo — o
            store do widget já tem `startNewConversation`, faltando expô-lo em `window.ModernWebWidget`.
            Ficou para rodada própria, com conferência no browser, porque errar aqui faz mensagem de
            uma análise anterior aparecer numa conversa nova. Num produto de compliance isso é bem
            pior que um pisca.
          */
          /*
            O bundle do widget só é baixado se o global NÃO existir.

            Mesma lógica do React acima: o global é o indicador real de "já está carregado", e agora
            ele SOBREVIVE à navegação (ver o efeito de desmontagem). Então entrar no chat pela segunda
            vez não baixa nem reinterpreta 1,2 MB — só remonta a UI.

            E isto continua correto para o caminho antigo: quando o host precisa recarregar de fato
            (bundle publicado sem `startNewConversation`), aquele caminho apaga o global de propósito
            antes de chegar aqui, então esta condição passa e o bundle é recarregado.
          */
          if (!window.ModernWebWidget) {
            await loadWidgetScript();
          }

          /*
            Inicializa IMEDIATAMENTE. Havia um `setTimeout(..., 1000)` aqui.

            Um segundo inteiro de tela em branco, por espera codificada — não por rede, não por
            processamento. E era espera desnecessária: `loadWidgetScript` resolve no `onload` do
            script, que só dispara DEPOIS de o bundle executar. Quando o `await` retorna,
            `window.ModernWebWidget` já existe. Não há corrida para o timeout proteger; ele só
            adiava o inevitável com a tela vazia.

            Se algum dia voltar a fazer falta, o certo não é o timeout de novo — é esperar a condição
            de verdade (o global existir), não um relógio.
          */
          if (!hasInitialized.current) {
            initializeWidget(userId);
            setupWidgetEventListeners(); // Configurar os event listeners apenas uma vez
          }
        } catch (error) {
          console.error("Erro ao carregar dependências:", error);
        }
      };

      const initializeWidget = (userId: string) => {
        // Obter o organizationId novamente para garantir que está atualizado
        const { userId: id, organizationId: orgId } = getUserData();
        
        // Se não tiver userId ou organizationId válidos, usar os valores passados ou defaults seguros
        const finalUserId = id || userId || 'default_user';
        const finalOrgId = orgId || 'default_org';
        
        // Gerar um novo conversationId inicial para a conversa
        const initialConversationId = `${finalOrgId}_${finalUserId}_${new Date().getTime()}`;
        
        // Armazenar o conversationId para ser usado em todas as mensagens desta conversa
        currentConversationId.current = initialConversationId;
        console.log('ConversationId inicial gerado:', currentConversationId.current);
        
          if (window.ModernWebWidget && typeof window.ModernWebWidget.initializeWidget === 'function') {
          // Configurações do widget
          configureWidget(finalUserId, finalOrgId, initialConversationId);
        }
      };

      // Função para configurar o widget com as opções desejadas
      const configureWidget = (userId: string, organizationId: string, conversationId: string) => {
        if (!window.ModernWebWidget || typeof window.ModernWebWidget.initializeWidget !== 'function') {
          console.error('Widget não está disponível para configuração');
          return;
        }
        
        const widgetConfig = {
          agentId: '679ab5f028fd2e21abd37162',
          client_id: userId,
          organization_id: organizationId,
          conversationId: conversationId,
          
          // Mensagem de boas-vindas para nova conversa
          welcome: {
            enabled: true,
            message: 'Nova submissão detectada! Esta é uma nova conversa, mas você ainda pode acessar seu histórico anterior.'
          },
          
          styles: WIDGET_STYLES
        };

        try {
          window.ModernWebWidget.initializeWidget(widgetConfig);
          hasInitialized.current = true;
          
          // Se for uma nova submissão, enviar uma mensagem de boas-vindas automaticamente
          if (newSubmission && window.ModernWebWidget && typeof window.ModernWebWidget.sendMessage === 'function') {
            // Esperar um pouco para garantir que o widget esteja completamente carregado
            setTimeout(async () => {
              const complianceData = processComplianceData(complianceResult);
              
              // Processar dados do setup se disponíveis
              const selectedPrompts = complianceResult?.selectedPrompts || JSON.parse(localStorage.getItem('selectedPrompts') || '[]');
              const selectedFolders = complianceResult?.selectedFolders || JSON.parse(localStorage.getItem('selectedFolders') || '[]');
              const complianceApiType = complianceResult?.complianceApiType || localStorage.getItem('complianceApiType');
              const complianceApiResponse = complianceResult?.complianceApiResponse || JSON.parse(localStorage.getItem('complianceApiResponse') || 'null');
              
              // Criar mensagem de boas-vindas baseada na API de compliance ou chat direto
              let messageContent = '';
              
              // Se for chat direto da sidebar
              if (complianceResult?.directChat) {
                messageContent = '💬 **Chat Iniciado!**\n\nOlá! Como posso ajudar você hoje?\n\nVocê pode fazer perguntas, solicitar análises ou usar os quick replies para selecionar prompts e pastas específicas.\n\n';
              } else if (complianceApiResponse && complianceApiType) {
                // Se temos resposta da API, usar como base da conversa
                messageContent = `✅ **${complianceApiType.toUpperCase().replace(/_/g, ' ')}** processado com sucesso!\n\n`;
                
                if (complianceApiResponse.result) {
                  messageContent += `📋 **Resultado da análise:**\n${complianceApiResponse.result}\n\n`;
                }
              } else {
                messageContent = 'Chat configurado! Como posso ajudar você hoje?\n\n';
              }
              
              // Adicionar prompts à mensagem se selecionados (apenas se não for chat direto)
              if (!complianceResult?.directChat && selectedPrompts && selectedPrompts.length > 0) {
                const promptsText = processSelectedPrompts(selectedPrompts);
                messageContent += '📝 **INSTRUÇÕES ATIVAS:**\n' + promptsText + '\n\n';
              }
              
              // Processar arquivos das pastas selecionadas (apenas se não for chat direto)
              let attachedFiles: any[] = [];
              if (!complianceResult?.directChat && selectedFolders && selectedFolders.length > 0) {
                attachedFiles = await processSelectedFolders(selectedFolders);
                if (attachedFiles.length > 0) {
                  messageContent += `📁 **ARQUIVOS CARREGADOS:** ${attachedFiles.length} arquivo(s) de ${selectedFolders.length} pasta(s) disponíveis para consulta.\n\n`;
                }
              }
              
              // Adicionar mensagem final apenas se não for chat direto
              if (!complianceResult?.directChat) {
                messageContent += '💬 **Digite sua pergunta ou solicite uma análise adicional...**';
              }
              
              // Obter modelo selecionado
              const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';
              
              const welcomeMessage = {
                type: 'SYSTEM_MESSAGE',
                content: messageContent,
                conversationId: conversationId,
                compliance_data: complianceData,
                complianceApiResponse: complianceApiResponse,
                complianceApiType: complianceApiType,
                selectedPrompts: selectedPrompts,
                attachedFiles: attachedFiles,
                model: selectedModel,
                isPreprocessed: true // Indica que já foi processado pela API
              };
              
              console.log('Enviando mensagem de boas-vindas com dados da API:', welcomeMessage);
              if (window.ModernWebWidget && typeof window.ModernWebWidget.sendMessage === 'function') {
                window.ModernWebWidget.sendMessage(welcomeMessage);
              }
              
              // Limpar dados do localStorage após envio
              localStorage.removeItem('selectedPrompts');
              localStorage.removeItem('selectedFolders');
              localStorage.removeItem('complianceApiType');
              localStorage.removeItem('complianceApiResponse');
            }, 2000); // Aguardar 2 segundos para garantir que o widget está pronto e expandido
          }
        } catch (error) {
          console.error("Erro ao inicializar widget:", error);
        }
      };

      loadDependencies();
    }

    /*
      A limpeza deste efeito remove APENAS o listener — nada de destruir o widget.

      Era aqui o motor principal do pisca. Este `return` derrubava tudo (elementos, script, o global
      `ModernWebWidget`, e zerava `hasInitialized`), e o React executa a limpeza de um efeito antes
      de CADA reexecução dele, não só na desmontagem. Como as dependências incluem `isVisible`, abrir
      o chat destruía o widget inteiro e forçava reconstrução — a janela em branco que aparecia no
      meio era exatamente esse intervalo.

      A destruição de verdade foi para um efeito próprio, com dependências vazias, logo abaixo: a
      limpeza dele roda só quando o componente desmonta, que é quando ela realmente deve rodar.
    */
    return () => {
      window.removeEventListener('message', handleWidgetMessage);
    };
  }, [isVisible, complianceResult, newSubmission]); // Adicionando complianceResult e newSubmission como dependências

  /*
    Desmontagem REAL do widget — efeito separado, dependências vazias.

    Com `[]`, a limpeza roda uma única vez, quando o componente sai da tela de verdade. É o lugar
    certo para liberar o widget: enquanto o componente vive, o widget deve sobreviver a mudança de
    prop, e é justamente o que faltava.
  */
  useEffect(() => {
    return () => {
      /*
        Some com o painel, mas PRESERVA o script e o global.

        Este componente é renderizado por `PendingPage` — uma página, não o layout. Então entrar no
        chat navega, o React Router monta a página, e o ChatWidget NASCE DE NOVO. Num nascimento
        `hasInitialized` volta a `false`, e é por isso que nenhuma guarda de reaproveitamento
        ajudava: o caminho completo rodava a cada entrada.

        Antes esta limpeza também removia a tag do script e apagava `window.ModernWebWidget`. Isso
        obrigava a próxima entrada a baixar e RE-EXECUTAR 1,2 MB de bundle antes de mostrar
        qualquer coisa — o pisca ao entrar no chat era esse intervalo.

        O widget vive em `document.body`, FORA da árvore React, então nada obriga o módulo a morrer
        junto com o componente. Preservando o global, a próxima entrada só remonta a UI: sem rede,
        sem reinterpretar o bundle.

        O que continua sendo removido é o CONTAINER, e isso é proposital — o painel precisa
        desaparecer quando se sai da página do chat.

        Efeito colateral desejável: o store do Zustand sobrevive, então o histórico da conversa
        continua ao voltar. Quando o pedido é conversa NOVA, quem zera é `startNewConversation`, que
        é a ferramenta certa para isso.
      */
      [
        '#modern-web-widget-container',
        'iframe[id*="modern-web-widget"]',
        'div[id*="modern-web-widget"]',
        '.modern-web-widget',
        '[class*="modern-web-widget"]'
      ].forEach(seletor => {
        document.querySelectorAll(seletor).forEach(el => el.remove());
      });
      // O React root foi embora com o container: na volta é preciso montar de novo.
      hasInitialized.current = false;
      currentConversationId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Criar uma função nomeada para o handler fora do setupWidgetEventListeners
  const handleWidgetMessage = (event: MessageEvent) => {
    // Verificar se a mensagem é do widget
    if (event.data && event.data.type === 'WIDGET_MESSAGE') {
      const complianceData = processComplianceData(complianceResult);
      
      // Obter os dados do usuário e usar o conversationId existente ou gerar um novo
      const { userId, organizationId } = getUserData();
      
      // Usar o conversationId existente ou gerar um novo se for a primeira mensagem
      if (!currentConversationId.current) {
        currentConversationId.current = `${organizationId}_${userId}_${new Date().getTime()}`;
        console.log('Novo conversationId gerado para conversa:', currentConversationId.current);
      }
      
      // Obter modelo selecionado
      const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';
      
      // Criar o objeto de mensagem com os dados da compliance
      const messageData = {
        type: 'USER_MESSAGE',
        content: event.data.content,
        compliance_data: complianceData,
        conversationId: currentConversationId.current,
        model: selectedModel
      };

      // Enviar a mensagem com os dados da compliance
      if (window.ModernWebWidget && window.ModernWebWidget.sendMessage) {
        window.ModernWebWidget.sendMessage(messageData);
      }
    }
    
    // Verificar se é uma solicitação para iniciar uma nova conversa
    if (event.data && event.data.type === 'NEW_CONVERSATION') {
      // Limpar o conversationId armazenado
      clearCurrentConversationId();
      
      const { userId, organizationId } = getUserData();
      // Gerar um novo conversationId para a nova conversa
      currentConversationId.current = `${organizationId}_${userId}_${new Date().getTime()}`;
      console.log('Novo conversationId gerado para nova conversa:', currentConversationId.current);
    }
    
    // Verificar se o widget foi completamente inicializado
    if (event.data && event.data.type === 'WIDGET_INITIALIZED') {
      console.log('Widget inicializado completamente');
      
      // Se for uma nova submissão, enviar uma mensagem de boas-vindas
      if (newSubmission && currentConversationId.current) {
        console.log('Enviando mensagem de boas-vindas para nova submissão após inicialização');
        
        // Usar setTimeout com função async para lidar com await
        setTimeout(async () => {
          // Processar dados do setup se disponíveis
          const selectedPrompts = complianceResult?.selectedPrompts || JSON.parse(localStorage.getItem('selectedPrompts') || '[]');
          const selectedFolders = complianceResult?.selectedFolders || JSON.parse(localStorage.getItem('selectedFolders') || '[]');
          
          const complianceData = processComplianceData(complianceResult);
          
          let messageContent = 'Uma nova submissão de compliance foi recebida. Como posso ajudar?';
          
          // Adicionar prompts à mensagem se selecionados
          if (selectedPrompts && selectedPrompts.length > 0) {
            const promptsText = processSelectedPrompts(selectedPrompts);
            messageContent += '\n\n📝 **INSTRUÇÕES SELECIONADAS:**\n' + promptsText;
          }
          
          // Processar arquivos das pastas selecionadas
          let attachedFiles: any[] = [];
          if (selectedFolders && selectedFolders.length > 0) {
            attachedFiles = await processSelectedFolders(selectedFolders);
            if (attachedFiles.length > 0) {
              messageContent += `\n\n📁 **ARQUIVOS PARA ANÁLISE:** ${attachedFiles.length} arquivo(s) de ${selectedFolders.length} pasta(s)`;
            }
          }
          
          // Obter modelo selecionado
          const selectedModel = localStorage.getItem('selectedGptModel') || 'gpt-4';
          
          const welcomeMessage = {
            type: 'SYSTEM_MESSAGE',
            content: messageContent,
            conversationId: currentConversationId.current,
            compliance_data: complianceData,
            selectedPrompts: selectedPrompts,
            attachedFiles: attachedFiles,
            model: selectedModel
          };
          
          if (window.ModernWebWidget && typeof window.ModernWebWidget.sendMessage === 'function') {
            window.ModernWebWidget.sendMessage(welcomeMessage);
          }
        }, 100);
      }
    }
  };

  return (
    <>
    </>
  );
};

export default ChatWidget; 